import React from 'react';
import { motion } from 'framer-motion';
import { Star, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useLocale } from '../context/LocaleContext';
import { useT } from '../hooks/useT';
import { sitePathSlug } from '../utils/sitePathSlug';

const Card = styled(motion.div)`
  background: ${({ theme }) => theme.colors.card};
  padding: 1.5rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  box-shadow: ${({ theme }) => theme.shadowCard};
  transition: box-shadow 0.2s;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  &:hover {
    box-shadow: ${({ theme }) => theme.shadowCardHover};
  }
`;

const CardTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const InitialBox = styled.div`
  width: 3rem;
  height: 3rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: rgba(0, 48, 135, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 700;
  font-size: ${({ theme }) => theme.fontClamp.lg};
`;

const ScoreBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.75rem;
  background: rgba(234, 179, 8, 0.1);
  color: ${({ theme }) => theme.colors.gold};
  border-radius: ${({ theme }) => theme.radiusSm};
  font-size: ${({ theme }) => theme.fontClamp.xs};
  font-weight: 700;
`;

const CardTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontClamp.lg};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.foreground};
  margin: 0;
`;

const CardDesc = styled.p`
  font-size: ${({ theme }) => theme.fontClamp.sm};
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin: 0.25rem 0 0 0;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const TagsWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const Tag = styled.span`
  padding: 0.25rem 0.75rem;
  background: rgba(128, 0, 0, 0.1);
  color: ${({ theme }) => theme.colors.maroon};
  font-size: ${({ theme }) => theme.fontClamp.xs};
  font-weight: 500;
  border-radius: ${({ theme }) => theme.radiusSm};
`;

const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  font-size: ${({ theme }) => theme.fontClamp.xs};
  color: ${({ theme }) => theme.colors.mutedForeground};
  & .tabular {
    font-variant-numeric: tabular-nums;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.foreground};
  }
`;

const AlternativeRank = styled.span`
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 600;
`;

const TrendingPill = styled.span`
  padding: 0.2rem 0.5rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  font-size: ${({ theme }) => theme.fontClamp.xs};
  font-weight: 600;
  background: ${({ $rising, $top, theme }) => ($top ? 'rgba(234,179,8,0.15)' : $rising ? 'rgba(34,197,94,0.15)' : 'rgba(0,0,0,0.06)')};
  color: ${({ $rising, $top, theme }) => ($top ? theme.colors.gold : $rising ? 'hsl(142,71%,45%)' : theme.colors.mutedForeground)};
`;

const UserScore = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.foreground};
`;

const CardLink = styled(Link)`
  width: 100%;
  padding: 0.75rem 0;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.primaryForeground};
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontClamp.sm};
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: opacity 0.2s;
  &:hover {
    opacity: 0.9;
  }
`;

function SiteCard({ site }) {
  const { prependPath } = useLocale();
  const t = useT();
  const initial = site.title.charAt(0).toUpperCase();

  return (
    <Card whileHover={{ y: -4 }} transition={{ duration: 0.2, ease: [0.2, 0, 0, 1] }}>
      <CardTop>
        <InitialBox>{initial}</InitialBox>
        {site.similarityScore && (
          <ScoreBadge>
            <Star size={12} fill="currentColor" />
            {(site.similarityScore / 10).toFixed(1)}
          </ScoreBadge>
        )}
      </CardTop>
      <div>
        <CardTitle>{site.domain}</CardTitle>
        <CardDesc>{site.description}</CardDesc>
      </div>
      <TagsWrap>
        {site.tags.map((tag) => (
          <Tag key={tag}>{tag}</Tag>
        ))}
      </TagsWrap>
      {(site.alternativeRank || site.trending || site.userScore) && (
        <Meta>
          {site.alternativeRank && site.alternativeTo && (
            <AlternativeRank>#{site.alternativeRank} alternative to {site.alternativeTo}</AlternativeRank>
          )}
          {site.trending && (
            <TrendingPill $rising={site.trending === 'Rising'} $top={site.trending === 'Top choice'}>
              {site.trending === 'Rising' && '↑ '}{site.trending}
            </TrendingPill>
          )}
          {site.userScore != null && (
            <span>
              <UserScore className="tabular">{site.userScore}/5</UserScore>
            </span>
          )}
        </Meta>
      )}
      <CardLink to={prependPath(`/similar/${sitePathSlug(site)}`)}>
        {t('common.viewSimilarSites')} <ArrowRight size={14} />
      </CardLink>
    </Card>
  );
}

export default SiteCard;
